import { api } from '@/global/services/api'
import {
  applyProviderStatusToPayload,
  toProviderStatus
} from '@/modules/road-maintenance-module/winter-service-v2/common/composables/providerData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  buildBasePhotoEndpoint,
  BASE_PHOTO_KEY
} from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'

/**
 * The backend reports a base nested - the address sits on `geolocation`, the contact
 * on `responsible_person`, the photo on `photo`, the status is an `is_active` flag -
 * while the forms edit one flat field per value. This is the single place that
 * translation lives, so the details form and the create form can never read or write
 * a base differently.
 */

/**
 * Backend record -> the flat keys the form fields are bound to.
 */
export function toBaseForm (record) {
  const {
    geolocation,
    responsible_person: responsiblePerson,
    photo
  } = record || {}

  return {
    address: geolocation?.address ?? null,
    latitude: geolocation?.latitude ?? null,
    longitude: geolocation?.longitude ?? null,
    status: toProviderStatus(record),
    responsible_person_name: responsiblePerson?.name ?? null,
    responsible_person_email: responsiblePerson?.email ?? null,
    responsible_person_phone: responsiblePerson?.phone ?? null,
    // The image panel renders whatever sits on this key - the stored URL until
    // the user picks a File over it.
    [BASE_PHOTO_KEY]: photo?.url ?? null
  }
}

/**
 * Writes the flat keys onto the record itself. Content.vue hands `onDataReceived` the
 * very object it then turns into the form data, so the flat values have to land on it
 * rather than on a copy - that also keeps the untouched copy it restores on cancel
 * flat, instead of emptying every field.
 */
export function applyBaseForm (record) {
  if (!record) {
    return record
  }

  return Object.assign(record, toBaseForm(record))
}

/**
 * The same translation as a fresh object. Used after a save: the record Content.vue
 * already holds has been made reactive, so keys added to it afterwards would not be
 * picked up - it has to be handed a new object instead.
 */
export function withBaseForm (record) {
  return {
    ...record,
    ...toBaseForm(record)
  }
}

/**
 * Form values -> what the upsert endpoint accepts. The status dropdown edits what the
 * backend keeps as `is_active`, and the photo has an endpoint of its own, so it is
 * dropped here rather than sent along.
 */
export function toBasePayload (data) {
  const payload = applyProviderStatusToPayload(data)

  if (payload) {
    delete payload[BASE_PHOTO_KEY]
  }

  return payload
}

/**
 * Reads a freshly picked photo out of the form values. Anything that is not a File is
 * the stored URL the panel was showing, which is nothing to upload.
 */
export function pickedBasePhoto (data) {
  const photo = data?.[BASE_PHOTO_KEY]

  return photo instanceof File ? photo : null
}

/**
 * Whether the user emptied the panel over a photo the base already had. Both sides
 * are needed: an empty panel on a base without a photo is simply untouched.
 */
export function clearedBasePhoto (data, storedPhotoUrl) {
  return !data?.[BASE_PHOTO_KEY] && !!storedPhotoUrl
}

/**
 * Sends the picked photo to its own endpoint and answers with the base as the backend
 * reports it afterwards, so the caller can refresh the form off a single record. One
 * photo per base - a new upload replaces the previous one.
 */
export async function uploadBasePhoto (baseId, photo) {
  if (!baseId || !photo) {
    return null
  }

  const response = await api(WINTER_SERVICE_MODULE).post(
    buildBasePhotoEndpoint(baseId),
    { file: photo }
  )

  return response?.data || null
}

/**
 * Drops the stored photo. The endpoint answers with a message rather than the base,
 * so the caller keeps the record it already has and only clears the photo off it.
 */
export async function deleteBasePhoto (baseId) {
  if (!baseId) {
    return
  }

  await api(WINTER_SERVICE_MODULE).delete(buildBasePhotoEndpoint(baseId))
}

/**
 * The URL the image panel was showing when the record arrived - what a later empty
 * panel is compared against to tell a cleared photo from one that was never there.
 */
export function storedBasePhotoUrl (record) {
  return record?.photo?.url ?? null
}
